import React from 'react'
import { Invoice, EmailTemplate } from '@/types/invoice'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { formatCurrency, getDaysOverdue } from '@/lib/utils'
import { Mail, X } from 'lucide-react'

interface EmailPreviewProps {
  invoice: Invoice
  template: EmailTemplate
  isOpen: boolean
  onClose: () => void
  onOpenEmail: () => void
}

export function EmailPreview({ invoice, template, isOpen, onClose, onOpenEmail }: EmailPreviewProps) {
  const processTemplate = (template: EmailTemplate, invoice: Invoice) => {
    const daysOverdue = getDaysOverdue(invoice.dueDate)
    const amount = formatCurrency(invoice.amount, invoice.currency)
    const dueDate = new Date(invoice.dueDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    let subject = template.subject
      .replace(/{client}/g, invoice.client)
      .replace(/{number}/g, invoice.number)
      .replace(/{amount}/g, amount)
      .replace(/{dueDate}/g, dueDate)
      .replace(/{daysOverdue}/g, daysOverdue.toString())
      .replace(/{description}/g, invoice.description || '')

    let body = template.body
      .replace(/{client}/g, invoice.client)
      .replace(/{number}/g, invoice.number)
      .replace(/{amount}/g, amount)
      .replace(/{dueDate}/g, dueDate)
      .replace(/{daysOverdue}/g, daysOverdue.toString())
      .replace(/{description}/g, invoice.description || '')

    return { subject, body }
  }

  const { subject, body } = processTemplate(template, invoice)

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Preview - {invoice.client}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invoice Info */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Invoice Details</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
              <div><strong>Client:</strong> {invoice.client}</div>
              <div><strong>Invoice:</strong> {invoice.number}</div>
              <div><strong>Amount:</strong> {formatCurrency(invoice.amount, invoice.currency)}</div>
              <div><strong>Due Date:</strong> {new Date(invoice.dueDate).toLocaleDateString()}</div>
              {invoice.description && (
                <div className="col-span-2"><strong>Description:</strong> {invoice.description}</div>
              )}
            </div>
          </div>

          {/* Email Preview */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Subject</label>
              <div className="p-3 bg-gray-50 border rounded-md font-medium">
                {subject}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Body</label>
              <div className="p-4 bg-gray-50 border rounded-md whitespace-pre-wrap text-sm">
                {body}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
            <Button onClick={onOpenEmail} className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Open in Email Program
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
} 